import type { I18nState } from '../../state/i18nStore';
import { useI18nStore } from '../../state/i18nStore';
import type { Locale } from '../../state/types';

import { loadTranslations } from './actions';
import { getFeatureTranslationKey } from './shared';

type SuspendWith = (thenable: unknown) => never;

/**
 * Suspends the calling component by throwing a thenable for React's Suspense to await. Centralises the
 * one place this codebase intentionally throws a non-error so the Suspense contract reads clearly.
 */
export const suspendWith: SuspendWith = (thenable) => {
  throw thenable;
};

const inFlightTranslationLoads = new Map<string, Promise<void>>();

type GetOrCreateTranslationLoadPromise = (locale: Locale, feature: string) => Promise<void>;

const hasTranslationLoadCompleted = (state: I18nState, featureId: string): boolean => {
  const status = state.translations[featureId]?.status;

  return status === 'fulfilled' || status === 'rejected';
};

const createTranslationLoadPromise = (locale: Locale, feature: string, featureId: string): Promise<void> =>
  new Promise<void>((resolve) => {
    const resolveWhenTranslationLoadCompletes = (state: I18nState) => {
      if (hasTranslationLoadCompleted(state, featureId)) {
        unsubscribe();
        resolve();
      }
    };

    const unsubscribe = useI18nStore.subscribe(resolveWhenTranslationLoadCompletes);
    resolveWhenTranslationLoadCompletes(useI18nStore.getState());
    void loadTranslations({ locale, feature }).catch(() => undefined);
  });

/**
 * Returns a stable promise for a feature's translation load, suitable for throwing to React Suspense.
 *
 * React requires the same promise instance to be thrown across re-renders for a given resource, so
 * in-flight loads are cached by `locale|feature`. A failed load resolves (rather than rejects) so the
 * suspending component re-renders and degrades to empty strings instead of escalating to an error
 * boundary. The cache entry is cleared once settled, by which point the store already holds the
 * `fulfilled`/`rejected` status, so the component will not suspend again.
 */
export const getOrCreateTranslationLoadPromise: GetOrCreateTranslationLoadPromise = (locale, feature) => {
  const featureId = getFeatureTranslationKey(locale, feature);

  let inFlight = inFlightTranslationLoads.get(featureId);
  if (!inFlight) {
    inFlight = createTranslationLoadPromise(locale, feature, featureId).finally(() => {
      inFlightTranslationLoads.delete(featureId);
    });
    inFlightTranslationLoads.set(featureId, inFlight);
  }

  return inFlight;
};
